import {
  Table,
  TableSortIcon,
  TagWithIcon,
  formatDate,
} from "@endya-conformite/ui-kit";
import ChevronDown from "@endya-conformite/ui-kit/src/assets/icons/chevron-down.svg?react";
import InfoIcon from "@endya-conformite/ui-kit/src/assets/icons/info-circle-fill.svg?react";
import RefreshIcon from "@endya-conformite/ui-kit/src/assets/icons/refresh.svg?react";
import CancelIcon from "@endya-conformite/ui-kit/src/assets/icons/x-circle-fill.svg?react";
import {
  TableSortValues,
  sortRows,
  useTableSort,
} from "@src/helper/table.helper";
import { TICKET_STATUS_MAP, TicketStatus } from "@src/helper/ticket.helper";
import classNames from "classnames";
import { useMemo, useState } from "react";
import { CQOriasRadiee } from "@src/helper/cq-orias-radiees.helper";
import style from "./CQOriasRadiees.module.scss";

type CQOriasRadieesProps = {
  rows?: CQOriasRadiee[];
  lastUpdate?: string;
  hasError?: boolean;
  isRelaunching?: boolean;
  onRelaunch?: () => void;
  selected?: string[];
  onToggleRow?: (id: string) => void;
  onClearSelection?: () => void;
  onCancelRows?: (rows: CQOriasRadiee[]) => void;
  onRowClick?: (row: CQOriasRadiee) => void;
  isCancelling?: boolean;
};

const TITRE_ERREUR = "Contrôles avec ORIAS Radié - Erreur de mise à jour";

const EXCLUDED_STAGES: CQOriasRadiee["pipelineStage"][] = [
  TicketStatus.FERME,
  TicketStatus.ANNULE,
  TicketStatus.RADIE,
];

const SORT_VALUES: TableSortValues<CQOriasRadiee> = {
  millesime: (row) => row.millesime,
  entrepriseName: (row) => row.entrepriseName,
  pipelineStage: (row) => TICKET_STATUS_MAP[row.pipelineStage].label,
  dateDebut: (row) => row.dateDebut,
  dateDepot: (row) => row.dateDepot,
  dateButoir: (row) => row.dateButoir,
  conformiteIa: (row) => row.conformiteIa,
  conformiteVerificateur: (row) => row.conformiteVerificateur,
  avancement: (row) =>
    row.avancementTotal === 0 ? 0 : row.avancementTraite / row.avancementTotal,
};

export function CQOriasRadiees({
  rows = [],
  lastUpdate,
  hasError = false,
  isRelaunching = false,
  onRelaunch,
  selected = [],
  onToggleRow,
  onClearSelection,
  onCancelRows,
  onRowClick,
  isCancelling = false,
}: CQOriasRadieesProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { sortBy, sortOrder, toggleSort, orderFor } = useTableSort();

  const visibleRows = useMemo(
    () => rows.filter((row) => !EXCLUDED_STAGES.includes(row.pipelineStage)),
    [rows]
  );

  const sortedRows = useMemo(
    () => sortRows(visibleRows, SORT_VALUES, { sortBy, sortOrder }),
    [visibleRows, sortBy, sortOrder]
  );

  const isExpanded = isOpen && !hasError;

  // Rien à signaler : aucune société radiée et aucun échec de collecte à
  // remonter, le bandeau n'a pas lieu d'être.
  if (visibleRows.length === 0 && !hasError) return null;

  const selectedRows = sortedRows.filter((row) => selected.includes(row.id));
  const rowsToCancel = selectedRows.length > 0 ? selectedRows : sortedRows;

  const relaunchButton = (label: string) => (
    <button
      type="button"
      className={style.relaunchButton}
      onClick={onRelaunch}
      disabled={isRelaunching}
    >
      {isRelaunching ? "Relance en cours..." : label}
      <RefreshIcon
        className={classNames(style.relaunchIcon, {
          [style.relaunchIconSpinning]: isRelaunching,
        })}
      />
    </button>
  );

  const toggleButton = (
    <button
      type="button"
      className={classNames(style.toggleButton, {
        [style.toggleButtonClosed]: !isOpen,
      })}
      onClick={() => setIsOpen((prev) => !prev)}
      aria-expanded={isOpen}
    >
      {isOpen ? "Masquer" : "Afficher"}
      <ChevronDown
        className={classNames(style.toggleChevron, {
          [style.toggleChevronOpen]: isOpen,
        })}
      />
    </button>
  );

  return (
    <section
      className={classNames(style.container, {
        [style.containerError]: hasError,
      })}
    >
      <div className={style.frame}>
        <div className={style.bannerRow}>
          <div
            className={classNames(style.banner, {
              [style.bannerClosed]: !isExpanded,
            })}
          >
            <InfoIcon className={style.bannerIcon} />
            <p className={style.bannerTitle}>
              {hasError
                ? TITRE_ERREUR
                : `Contrôles avec ORIAS Radié (${visibleRows.length})`}
            </p>
            {lastUpdate && (
              <p className={style.bannerUpdate}>
                Dernière mise à jour : {formatDate(new Date(lastUpdate))}
              </p>
            )}
            {!hasError && toggleButton}
          </div>
          {hasError &&
            relaunchButton("Erreur : Relancer la récolte de données")}
        </div>
        <div
          className={classNames(style.collapse, {
            [style.collapseOpen]: isExpanded,
          })}
        >
          <div className={style.collapseInner}>
            <Table
              className={style.table}
              data={sortedRows}
              flatDesign
              noDarkenColor
              onRowClick={onRowClick}
              sort={toggleSort}
              textNoData="Aucun contrôle avec une société radiée de l'ORIAS"
              columns={[
                {
                  key: "millesime",
                  title: "Millésime",
                  icon: <TableSortIcon order={orderFor("millesime")} />,
                  size: 6,
                  className: style.bodyCell,
                  render: ({ id, entrepriseName, millesime }) => (
                    <span className={style.millesimeCell}>
                      <input
                        type="checkbox"
                        className={style.checkbox}
                        checked={selected.includes(id)}
                        aria-label={`Sélectionner ${entrepriseName}`}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => onToggleRow?.(id)}
                      />
                      <p>{millesime}</p>
                    </span>
                  ),
                },
                {
                  key: "entrepriseName",
                  title: "Entreprise",
                  icon: <TableSortIcon order={orderFor("entrepriseName")} />,
                  size: 8,
                  className: style.bodyCell,
                  render: ({ entrepriseName }) => (
                    <p className={style.entreprise}>{entrepriseName}</p>
                  ),
                },
                {
                  key: "pipelineStage",
                  title: "État",
                  icon: <TableSortIcon order={orderFor("pipelineStage")} />,
                  size: 8,
                  className: style.bodyCell,
                  render: ({ pipelineStage }) => {
                    const config = TICKET_STATUS_MAP[pipelineStage];
                    return (
                      <TagWithIcon
                        overrideIcon={config.icon}
                        variant={config.variant}
                      >
                        {config.label}
                      </TagWithIcon>
                    );
                  },
                },
                {
                  key: "dateDebut",
                  title: "Date de début",
                  icon: <TableSortIcon order={orderFor("dateDebut")} />,
                  size: 6,
                  className: style.bodyCell,
                  render: ({ dateDebut }) => (
                    <p>{formatDate(new Date(dateDebut))}</p>
                  ),
                },
                {
                  key: "dateDepot",
                  title: "Date de dépôt",
                  icon: <TableSortIcon order={orderFor("dateDepot")} />,
                  size: 6,
                  className: style.bodyCell,
                  render: ({ dateDepot }) => (
                    <p>{dateDepot ? formatDate(new Date(dateDepot)) : "-"}</p>
                  ),
                },
                {
                  key: "dateButoir",
                  title: "Date butoir",
                  icon: <TableSortIcon order={orderFor("dateButoir")} />,
                  size: 6,
                  className: style.bodyCell,
                  render: ({ dateButoir }) => (
                    <p>{dateButoir ? formatDate(new Date(dateButoir)) : "-"}</p>
                  ),
                },
                {
                  key: "conformiteIa",
                  title: "Conformité IA",
                  icon: <TableSortIcon order={orderFor("conformiteIa")} />,
                  size: 6,
                  className: style.bodyCell,
                  render: ({ conformiteIa }) =>
                    conformiteIa === null ? (
                      <p className={style.pending}>-</p>
                    ) : (
                      <p className={style.conformiteIa}>{conformiteIa}%</p>
                    ),
                },
                {
                  key: "conformiteVerificateur",
                  title: "Conformité vérificateur",
                  icon: (
                    <TableSortIcon order={orderFor("conformiteVerificateur")} />
                  ),
                  size: 7,
                  className: style.bodyCell,
                  render: ({ conformiteVerificateur }) =>
                    conformiteVerificateur === null ? (
                      <p className={style.pending}>-</p>
                    ) : (
                      <p className={style.conformiteVerificateur}>
                        {conformiteVerificateur}%
                      </p>
                    ),
                },
                {
                  key: "avancement",
                  title: "Avancée du traitement",
                  icon: <TableSortIcon order={orderFor("avancement")} />,
                  size: 7,
                  className: style.bodyCell,
                  render: ({ avancementTraite, avancementTotal }) =>
                    avancementTotal === 0 ? (
                      <p className={style.pending}>-</p>
                    ) : (
                      <p
                        className={classNames(style.avancement, {
                          [style.avancementComplete]:
                            avancementTraite === avancementTotal,
                        })}
                      >
                        {avancementTraite}/{avancementTotal}
                      </p>
                    ),
                },
              ]}
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(style.collapse, {
          [style.collapseOpen]: isExpanded,
        })}
      >
        <div className={classNames(style.collapseInner, style.footerActions)}>
          {selectedRows.length > 0 && (
            <button
              type="button"
              className={style.clearSelectionButton}
              onClick={onClearSelection}
            >
              Retour
            </button>
          )}
          <button
            type="button"
            className={style.cancelAllButton}
            disabled={isCancelling || rowsToCancel.length === 0}
            onClick={() => onCancelRows?.(rowsToCancel)}
          >
            <CancelIcon />
            {isCancelling && "Annulation en cours..."}
            {!isCancelling &&
              (selectedRows.length > 0
                ? `Annuler les contrôles sélectionnés (${selectedRows.length})`
                : "Annuler tous les contrôles")}
          </button>
        </div>
      </div>
    </section>
  );
}
